// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Accounting Postings LIST page (Stage 6P.6).
//
// Server-paginated list at /erp/purchase-accounting/postings.
//
// Columns:
//   Posting No · Posting Date · Purchase Bill No · Supplier Invoice No
//   Supplier (+ GSTIN tag) · Voucher No · Taxable Amount · GST
//   Grand Total · Posting Status · Posted At · Actions (⋯)
//
// Toolbar:
//   Search (q over posting_no / bill_no / invoice_no / supplier_name)
//   Posting status filter · Date range (posting_date) · Clear · Refresh
//
// Per-row actions:
//   • View   → navigate to /erp/purchase-accounting/postings/:id
//   • Cancel → only available when posting_status === POSTED
//              (backend reverses ledger impact + sets cancelled_at)
//
// Backend remains the authority for ALL accounting math:
//   ledger code resolution, debit/credit balance, round-off
//   classification, ITC eligibility, duplicate-block decisions.
// This page is a pure read + dispatch surface.
//
// Style:
//   Mirrors PurchaseInwardPage / PurchaseBillsPage — slate-50 header
//   strip, 22 px row density, sticky toolbar, ⋯ row-actions menu.
//   Reuses the shared GoodsReceiptsPage.css via the .grn-* wrapper so
//   no new CSS file is added.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Dropdown, Input, Modal, Select,
  Space, Spin, Table, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, MoreOutlined,
  ExclamationCircleOutlined, EyeOutlined, StopOutlined,
  AuditOutlined
} from '@ant-design/icons';

import { purchaseAccountingApi } from './purchaseAccountingApi';
import {
  POSTING_STATUSES,
  accountingStatusLabel,
  accountingStatusColor,
  canCancelPurchaseAccountingPosting
} from './purchaseAccountingHelpers';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;


// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

const STATUS_OPTIONS = POSTING_STATUSES.map((v) => ({
  value: v,
  label: accountingStatusLabel(v)
}));


// ─────────────────────────────────────────────────────────────────
// Pure helpers — kept local so the page doesn't depend on a shared
// formatter module that could drift.
// ─────────────────────────────────────────────────────────────────

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

function fmtDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  const hh    = String(d.getHours()).padStart(2, '0');
  const mm    = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hh}:${mm}`;
}

// ₹ grouping is locale-aware (en-IN) so 12,34,567.89 is rendered the
// same way as elsewhere on the control plane. Falsy / non-finite
// inputs collapse to an em-dash so empty rows don't read as "₹0".
function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// Compose the "GST" column = CGST + SGST + IGST. The backend already
// emits total_tax_amount; we prefer it when present and fall back to a
// summed expression so a tenant that returns the parts but not the
// pre-summed total still renders a sensible number.
function readGstTotal(row) {
  if (!row) return null;
  const total = pickField(row, 'total_tax_amount', 'totalTaxAmount');
  if (total !== undefined && total !== null && total !== '') {
    const n = Number(total);
    if (Number.isFinite(n)) return n;
  }
  const cgst = Number(pickField(row, 'cgst_amount', 'cgstAmount') || 0);
  const sgst = Number(pickField(row, 'sgst_amount', 'sgstAmount') || 0);
  const igst = Number(pickField(row, 'igst_amount', 'igstAmount') || 0);
  if (!Number.isFinite(cgst) && !Number.isFinite(sgst) && !Number.isFinite(igst)) {
    return null;
  }
  return (Number.isFinite(cgst) ? cgst : 0)
    + (Number.isFinite(sgst) ? sgst : 0)
    + (Number.isFinite(igst) ? igst : 0);
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function PurchaseAccountingPostingsPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit
  // `erp.purchase.accounting.*` strings are the Stage 6P.6 perms.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView   = hasPerm('erp.purchase.accounting.view');
  const canCancel = hasPerm('erp.purchase.accounting.cancel');

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [search,      setSearch]      = useState('');
  const [status,      setStatus]      = useState(null);
  const [dateRange,   setDateRange]   = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId,  setBusyId]  = useState(null);

  // ── Debounced search wiring ──────────────────────────────────
  // Keep the visible input snappy, but only re-fetch once the
  // operator pauses typing — same pattern as the bills list.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = { page, pageSize };
      const needle = (search || '').trim();
      // Send under BOTH `q` and `search` — backend tolerates the
      // unused one via stripUnknown:true on its Joi validator.
      if (needle) {
        params.q      = needle;
        params.search = needle;
      }
      if (status) params.status = status;
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.fromDate = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.toDate   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await purchaseAccountingApi.listPostings(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : Array.isArray(out?.items) ? out.items
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setRows(list);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load purchase accounting postings.'),
        key: 'pap-list'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, page, pageSize, search, status, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onStatus = (v) => { setStatus(v || null);    setPage(1); };
  const onDates  = (v) => { setDateRange(v || null); setPage(1); };
  const onReset  = () => {
    setSearchInput('');
    setSearch('');
    setStatus(null);
    setDateRange(null);
    setPage(1);
  };

  // ── Row navigation ───────────────────────────────────────────
  const openView = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    navigate(`/erp/purchase-accounting/postings/${encodeURIComponent(id)}`);
  }, [navigate]);

  // ── Cancel handler — confirmed via Modal with a reason prompt ─
  const runCancel = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    let reason = '';
    Modal.confirm({
      title: 'Cancel accounting posting?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p style={{ marginBottom: 8, fontSize: 12.5 }}>
            This will reverse the ledger impact of posting{' '}
            <strong>{pickField(row, 'posting_no', 'postingNo') || id}</strong>{' '}
            and mark it cancelled. Provide a short reason (optional).
          </p>
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="Reason (optional)"
            onChange={(e) => { reason = e.target.value; }}
          />
        </div>
      ),
      okText: 'Cancel Posting',
      okButtonProps: { danger: true },
      cancelText: 'Keep',
      async onOk() {
        setBusyId(id);
        try {
          await purchaseAccountingApi.cancelPosting(id, reason || undefined);
          message.success('Posting cancelled.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to cancel posting.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  // ── Row-action menu builder ──────────────────────────────────
  const buildRowMenu = useCallback((row) => {
    const items = [];
    items.push({
      key:   'view',
      icon:  <EyeOutlined />,
      label: 'View'
    });

    const postingStatus = pickField(row, 'posting_status', 'postingStatus');
    if (canCancel && canCancelPurchaseAccountingPosting(postingStatus)) {
      items.push({ type: 'divider' });
      items.push({
        key:    'cancel',
        icon:   <StopOutlined />,
        label:  'Cancel',
        danger: true
      });
    }
    return items;
  }, [canCancel]);

  const onRowMenu = useCallback((key, row) => {
    switch (key) {
      case 'view':   return openView(row);
      case 'cancel': return runCancel(row);
      default:       return undefined;
    }
  }, [openView, runCancel]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Posting No',
      dataIndex: 'postingNo',
      width:     180,
      render: (_v, row) => {
        const no = pickField(row, 'posting_no', 'postingNo') || '—';
        return (
          <button
            type="button"
            className="grn-link"
            onClick={() => openView(row)}
          >
            {no}
          </button>
        );
      }
    },
    {
      title:     'Posting Date',
      dataIndex: 'postingDate',
      width:     120,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDate(pickField(row, 'posting_date', 'postingDate'))}
        </span>
      )
    },
    {
      title:     'Purchase Bill No',
      dataIndex: 'purchaseBillNo',
      width:     160,
      render: (_v, row) => {
        const billNo = pickField(row, 'purchase_bill_no', 'purchaseBillNo');
        if (!billNo) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{billNo}</span>;
      }
    },
    {
      title:     'Supplier Invoice No',
      dataIndex: 'supplierInvoiceNo',
      width:     170,
      render: (_v, row) => {
        const ref = pickField(row, 'supplier_invoice_no', 'supplierInvoiceNo');
        if (!ref) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{ref}</span>;
      }
    },
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name  = pickField(row, 'supplier_name',  'supplierName')  || '—';
        const gstin = pickField(row, 'supplier_gstin', 'supplierGstin');
        return (
          <div className="grn-supplier">
            <Tooltip title={name}>
              <span className="grn-supplier__name">{name}</span>
            </Tooltip>
            {gstin
              ? <Tag color="blue" className="grn-gstin-tag">{gstin}</Tag>
              : null}
          </div>
        );
      }
    },
    {
      title:     'Voucher No',
      dataIndex: 'voucherNo',
      width:     150,
      render: (_v, row) => {
        const v = pickField(row, 'voucher_no', 'voucherNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Taxable Amount',
      dataIndex: 'taxableAmount',
      width:     140,
      align:     'right',
      render: (_v, row) => {
        const amt = pickField(row, 'taxable_amount', 'taxableAmount');
        return (
          <span className="grn-mono grn-line-count">{fmtMoney(amt)}</span>
        );
      }
    },
    {
      title:     'GST',
      dataIndex: 'totalTaxAmount',
      width:     120,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(readGstTotal(row))}
        </span>
      )
    },
    {
      title:     'Grand Total',
      dataIndex: 'grandTotal',
      width:     140,
      align:     'right',
      render: (_v, row) => {
        const amt = pickField(row, 'grand_total', 'grandTotal');
        return (
          <span className="grn-mono grn-line-count">{fmtMoney(amt)}</span>
        );
      }
    },
    {
      title:     'Posting Status',
      dataIndex: 'postingStatus',
      width:     150,
      render: (_v, row) => {
        const v = pickField(row, 'posting_status', 'postingStatus');
        if (!v) return (
          <Tag className="grn-status-tag" color="default">Not Posted</Tag>
        );
        return (
          <Tag color={accountingStatusColor(v)} className="grn-status-tag">
            {accountingStatusLabel(v) || String(v).replace(/_/g, ' ')}
          </Tag>
        );
      }
    },
    {
      title:     'Posted At',
      dataIndex: 'postedAt',
      width:     160,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDateTime(pickField(row, 'posted_at', 'postedAt'))}
        </span>
      )
    },
    {
      title:     '',
      key:       '_actions',
      width:     56,
      fixed:     'right',
      align:     'center',
      render: (_v, row) => {
        const items = buildRowMenu(row);
        const id = pickField(row, 'id', 'id');
        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                onRowMenu(key, row);
              }
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="grn-actions-btn"
              icon={<MoreOutlined />}
              loading={busyId === id}
              onClick={(e) => e.stopPropagation()}
              aria-label="Row actions"
            />
          </Dropdown>
        );
      }
    }
  ]), [openView, buildRowMenu, onRowMenu, busyId]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view purchase accounting postings."
          description="Ask your master account to grant erp.purchase.accounting.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><AuditOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Purchase Accounting Postings</span>
            <span className="grn-headstrip__subtitle">
              Track approved purchase bills as they post into the general
              ledger. Backend owns ledger resolution, debit/credit balance,
              ITC eligibility, and duplicate-block decisions — this surface
              reads and dispatches cancel actions only.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search posting no, bill no, invoice no, supplier…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            // Flush debounce on Enter.
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 320 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All posting statuses"
          value={status || undefined}
          onChange={onStatus}
          options={STATUS_OPTIONS}
          style={{ width: 200 }}
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 240 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) =>
              pickField(r, 'id', 'id')
              || pickField(r, 'posting_no', 'postingNo')}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => openView(row)
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No purchase accounting postings yet.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
